import React, { ReactNode, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchMaintenanceStatus } from "../../redux/slices/maintenanceSlice";
import { usePermission } from "../../hooks/usePermission";

interface ProtectedRouteProps {
  allowedRoles?: Array<"ENTERPRISE" | "ADMIN" | "CUSTOMER" | "SUPERADMIN" | "REVIEWER">;
  requiredPermission?: string;
  children?: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedRoles,
  requiredPermission,
  children,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { currentUser, loading } = useSelector((state: RootState) => state.auth);
  const { isInMaintenance, isLoading: isMaintenanceLoading } = useSelector(
    (state: RootState) => state.maintenance
  );
  const { hasPermission } = usePermission();

  const isCustomer = currentUser?.role?.role_code === "CUSTOMER";

  useEffect(() => {
    dispatch(fetchMaintenanceStatus());
  }, [dispatch]);

  if (isMaintenanceLoading || loading) {
    return null;
  }

  if (isInMaintenance && isCustomer) {
    return <Navigate to="/maintenance" replace />;
  }

  // Permission-based check (admin module)
  if (requiredPermission) {
    if (currentUser && !hasPermission(requiredPermission)) {
      return <Navigate to="/unauthorized" replace />;
    }
    return children ? <>{children}</> : <Outlet />;
  }

  // Role-based check (customer routes + fallback)
  if (
    currentUser &&
    currentUser.role?.role_code &&
    allowedRoles &&
    !allowedRoles.includes(currentUser.role.role_code)
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
